import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Info, MapPin, CreditCard, Power, Building2, Unlink, FileText, FilePlus, Barcode, UserMinus, UserPlus } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import type { DropdownItem } from '@/components/ui/DropdownMenu'
import { Pagination } from '@/components/ui/Pagination'
import { Badge, StatusBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { Select } from '@/components/ui/Select'
import { AsyncSelect, type AsyncOption } from '@/components/ui/AsyncSelect'
import { api } from '@/services/api/AxiosBaseService'
import { PlansService } from '@/services/modules/PlansService'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { StatusDot } from '@/components/ui/StatusDot'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { SubscribersService, type Subscriber, type DependentPayload, type EligiblePlan } from '@/services/modules/SubscribersService'
import { CompaniesService, type Company } from '@/services/modules/CompaniesService'
import { LookupService, type MaritalStatus, type DegreeRelation } from '@/services/modules/LookupService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'
import { extractApiError } from '@/lib/api-errors'
import { maskCpfPartial } from '@/lib/format'

const GENDER_OPTIONS = [
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'FEMININO', label: 'Feminino' },
  { value: 'OUTRO', label: 'Outro' },
]

/** Grupo de botões por tipo — padrão Todos. */
const TYPE_FILTERS = [
  { value: '', label: 'Todos' },
  { value: 'TITULAR', label: 'Titulares' },
  { value: 'DEPENDENTE', label: 'Dependentes' },
]

/** Cidade/UF a partir do endereço inline. */
function cityUf(s: Subscriber): string {
  const c = s.address?.city
  if (!c) return '—'
  return c.state ? `${c.city}/${c.state.abbreviation}` : c.city
}

export function SubscribersListPage() {
  const navigate = useNavigate()
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({
    searchPlaceholder: 'Buscar assinante',
    extraFields: [
      { key: 'cpf[like]', label: 'CPF', placeholder: '000.000.000-00', mask: maskCpfPartial, toValue: (v) => v.replace(/\D+/g, '') },
    ],
  })
  const [typeFilter, setTypeFilter] = useState('')
  const filters = useMemo(
    () => ({ ...sf.filters, ...(typeFilter ? { type: typeFilter } : {}) }),
    [sf.filters, typeFilter],
  )
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(SubscribersService, { size: 25, reloadKey: reload, filters })
  const [reissue, setReissue] = useState<Subscriber | null>(null)
  // Cada ação tem a sua permissão — ver a lista não dá direito de gerar 2ª via,
  // trocar empresa ou inativar. O backend exige o mesmo em cada rota.
  const { can } = useAuthority()
  const podeCriar = can('commercial.subscribers.create')
  const podeEditar = can('commercial.subscribers.edit')
  // Editar dependente pela lista usa PUT subscribers/{id}/dependents/{depId}.
  const podeDependentes = can('commercial.subscribers.dependents')
  const podeStatus = can('commercial.subscribers.status')
  const podeCarteirinha = can('commercial.subscribers.membership_card')
  const podeVincular = can('commercial.subscribers.link_company')
  const podeConverter = can('commercial.subscribers.convert')
  const podePagamento = can('commercial.subscribers.payment_method')
  const podeFaturas = can('commercial.subscribers.invoices')
  const podeVerFaturas = can('financial.revenues.view')
  const [editDep, setEditDep] = useState<Subscriber | null>(null)
  const [unlinkTarget, setUnlinkTarget] = useState<Subscriber | null>(null)
  const [changeTarget, setChangeTarget] = useState<Subscriber | null>(null)
  const [linkTarget, setLinkTarget] = useState<Subscriber | null>(null)
  const [batchTarget, setBatchTarget] = useState<Subscriber | null>(null)
  const [cardTarget, setCardTarget] = useState<Subscriber | null>(null)
  const [boletoTarget, setBoletoTarget] = useState<Subscriber | null>(null)
  const [makeDepTarget, setMakeDepTarget] = useState<Subscriber | null>(null)
  const [makeHolderTarget, setMakeHolderTarget] = useState<Subscriber | null>(null)
  const [busy, setBusy] = useState(false)

  function refresh() { setReload(v => v + 1) }

  const exp = useDataTableExport({
    filename: 'assinantes',
    service: SubscribersService,
    filters,
    columns: [
      { header: 'Código',      accessor: (s: Subscriber) => s.code ?? '' },
      { header: 'Nome',        accessor: (s: Subscriber) => s.name },
      { header: 'CPF',         accessor: (s: Subscriber) => s.formatted?.cpf ?? s.cpf ?? '' },
      { header: 'Data de nascimento', accessor: (s: Subscriber) => s.formatted?.birthDate ?? '' },
      { header: 'E-mail',      accessor: (s: Subscriber) => s.email ?? '' },
      { header: 'Celular',     accessor: (s: Subscriber) => s.formatted?.mobile ?? s.mobile ?? '' },
      { header: 'Plano',       accessor: (s: Subscriber) => s.plan?.name ?? '' },
      { header: 'Tipo',        accessor: (s: Subscriber) => s.type },
      { header: 'Cidade',      accessor: (s: Subscriber) => cityUf(s) },
      { header: 'Carteirinha', accessor: (s: Subscriber) => s.formatted?.membershipCardNumber ?? s.membershipCardNumber ?? '' },
      { header: 'Status',      accessor: (s: Subscriber) => s.status },
    ],
  })

  async function confirmReissue() {
    if (!reissue) return
    setBusy(true)
    try {
      const updated = await SubscribersService.reissueCard(reissue.id)
      toast.success({ title: '2ª via gerada', description: `Novo número: ${updated.formatted?.membershipCardNumber ?? updated.membershipCardNumber}` })
      setReissue(null); refresh()
    } catch (e) {
      toast.error({ title: 'Não foi possível gerar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function confirmSwitchBoleto() {
    if (!boletoTarget) return
    setBusy(true)
    try {
      await SubscribersService.switchToBoleto(boletoTarget.id)
      toast.success({ title: 'Forma de pagamento alterada', description: 'Assinatura cancelada no Asaas e marcada como boleto.' })
      setBoletoTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Não foi possível mudar para boleto', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function toggleStatus(s: Subscriber) {
    const next = s.status === 'ATIVO' ? 'INATIVO' : 'ATIVO'
    try {
      // Titular leva os dependentes junto — o retorno diz quantos, porque a
      // mudança acontece fora da linha que o usuário clicou.
      const { dependents } = await SubscribersService.setStatusCascading(s.id, next)
      const juntos = dependents === 0 ? '' : dependents === 1
        ? ' 1 dependente acompanhou.'
        : ` ${dependents} dependentes acompanharam.`
      toast.success({ title: 'Status atualizado', description: `${s.name} agora está ${next}.${juntos}` })
      refresh()
    } catch (e) {
      toast.error({ title: 'Não foi possível alterar o status', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<Subscriber>[] = [
    // 01 — Info (esquerda) + nome/código. Sem avatar.
    { key: 'name', header: 'Nome', sortable: true, alwaysVisible: true, className: 'w-full', render: s => (
      <div className="flex items-center gap-3">
        <InfoPopover
          align="left"
          ariaLabel="Detalhes do assinante"
          trigger={<Info size={16} className="text-gray-400 hover:text-brand-500 transition" />}
          title="Detalhes"
        >
          {/* Plano em destaque, antes do e-mail. Dependente herda do titular. */}
          <div className="mb-2 rounded-md bg-brand-50 px-2.5 py-1.5 ring-1 ring-brand-100">
            <div className="text-[10px] uppercase tracking-wide text-brand-600">Plano</div>
            <div className="text-sm font-semibold text-brand-700">
              {(s.type === 'DEPENDENTE' ? (s.parent?.plan?.name ?? s.plan?.name) : s.plan?.name) || '—'}
            </div>
            {s.type === 'DEPENDENTE' && (
              <div className="text-[11px] text-gray-500">Titular: {s.parent?.name ?? '—'}</div>
            )}
          </div>
          <dl className="space-y-2 text-xs">
            <InfoRow label="E-mail" value={(s.type === 'DEPENDENTE' ? (s.parent?.email ?? s.email) : s.email) || '—'} />
            <InfoRow label="Gênero" value={s.gender || '—'} />
            {s.type === 'TITULAR' && (
              s.company
                ? <InfoRow label="Empresa" value={s.company.name} />
                : <InfoRow label="Gestor" value={s.manager?.name || '—'} />
            )}
            {s.companyBranch && (
              <InfoRow label="Filial" value={s.companyBranch.systemName || s.companyBranch.corporateName || '—'} />
            )}
            <InfoRow label="Cadastrado em" value={s.formatted?.createdAt || '—'} />
            <InfoRow label="Atualizado em" value={s.formatted?.updatedAt || '—'} />
            <InfoRow label="Carteirinha" value={s.formatted?.membershipCardNumber || s.membershipCardNumber || '—'} />
            {/* Quando foi inativado — só faz sentido para quem está inativo. */}
            {s.status === 'INATIVO' && (
              <InfoRow
                label="Inativado em"
                value={s.formatted?.inactivatedAt || 'Data não registrada'}
              />
            )}
          </dl>
        </InfoPopover>
        <div className="min-w-0">
          <div className="font-medium text-gray-800">{s.name}</div>
          <div className="text-[11px] text-gray-500">#{s.code}</div>
        </div>
      </div>
    )},
    { key: 'cpf',    header: 'CPF', align: 'center', className: 'w-36 whitespace-nowrap',
      render: s => <span className="text-xs">{s.formatted?.cpf || '—'}</span> },
    { key: 'mobile', header: 'Contato', align: 'center', className: 'whitespace-nowrap',
      render: s => <span className="text-xs text-gray-500">{s.formatted?.mobile || s.email || '—'}</span> },
    { key: 'type',   header: 'Tipo', align: 'center', className: 'w-16',
      render: s => (
        <Badge variant={s.type === 'TITULAR' ? 'blue' : 'gray'}>
          <span title={s.type}>{s.type === 'TITULAR' ? 'T' : 'D'}</span>
        </Badge>
      ) },
    // 02 — Cidade/UF no lugar da antiga coluna de carteirinha.
    { key: 'city',   header: 'Cidade', align: 'center', className: 'w-44 whitespace-nowrap',
      render: s => {
        const v = cityUf(s)
        return v === '—'
          ? <span className="text-gray-400">—</span>
          : <span className="inline-flex items-center gap-1.5 text-gray-700"><MapPin size={13} className="text-brand-500 shrink-0" />{v}</span>
      } },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: s => (
        s.status === 'ATIVO' || s.status === 'INATIVO' ? (
          <StatusDot value={s.status} />
        ) : (
          <StatusBadge status={s.status} />
        )
      ),
    },
  ]

  function rowActions(s: Subscriber): DropdownItem[] {
    // Inativos: somente "Ativar".
    if (s.status !== 'ATIVO') {
      return podeStatus
        ? [{ key: 'activate', label: 'Ativar', icon: <Power size={14} />, onClick: () => toggleStatus(s) }]
        : []
    }

    const reissueItem: DropdownItem[] = podeCarteirinha
      ? [{ key: 'reissue', label: 'Gerar 2ª via de cartão', icon: <CreditCard size={14} />, onClick: () => setReissue(s) }]
      : []
    const inativarItem: DropdownItem[] = podeStatus
      ? [{ key: 'status', label: 'Inativar', icon: <Power size={14} />, danger: true, divider: true, onClick: () => toggleStatus(s) }]
      : []

    // Dependente ativo: editar (modal) · 2ª via · inativar.
    if (s.type === 'DEPENDENTE') {
      return [
        ...(podeDependentes ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => setEditDep(s) }] : []),
        ...(podeConverter ? [{ key: 'make-holder', label: 'Tornar titular', icon: <UserPlus size={14} />, onClick: () => setMakeHolderTarget(s) }] : []),
        ...reissueItem,
        ...inativarItem,
      ]
    }

    // Titular ativo.
    const items: DropdownItem[] = podeEditar
      ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => navigate(`/commercial/subscribers/${s.id}/editar`) }]
      : []

    if (s.company) {
      // Titular vinculado a empresa.
      if (podeVincular) {
        items.push(
          { key: 'unlink', label: 'Desvincular empresa', icon: <Unlink size={14} />, divider: true, onClick: () => setUnlinkTarget(s) },
          { key: 'change', label: 'Trocar empresa', icon: <Building2 size={14} />, onClick: () => setChangeTarget(s) },
        )
      }
    } else {
      // Titular individual.
      if (podeVerFaturas) {
        items.push({ key: 'invoices', label: 'Faturas', icon: <FileText size={14} />, divider: true, onClick: () => navigate(`/commercial/subscribers/${s.id}/faturas`) })
      }
      if (podeFaturas) {
        items.push({ key: 'batch', label: 'Gerar faturas em lote', icon: <FilePlus size={14} />, onClick: () => setBatchTarget(s) })
      }
      if (podePagamento) {
        items.push(s.paymentMethod === 'CARTÃO DE CRÉDITO'
          ? { key: 'to-boleto', label: 'Mudar para boleto', icon: <Barcode size={14} />, onClick: () => setBoletoTarget(s) }
          : { key: 'to-card', label: 'Mudar para cartão de crédito', icon: <CreditCard size={14} />, onClick: () => setCardTarget(s) })
      }
      if (podeVincular) {
        items.push({ key: 'link', label: 'Vincular empresa', icon: <Building2 size={14} />, onClick: () => setLinkTarget(s) })
      }
    }

    if (podeConverter) {
      items.push({ key: 'make-dependent', label: 'Tornar dependente', icon: <UserMinus size={14} />, onClick: () => setMakeDepTarget(s) })
    }
    items.push(...reissueItem, ...inativarItem)
    return items
  }

  return (
    <>
      <PageHeader
        title="Assinantes"
        subtitle="Titulares e dependentes"
        crumbs={[{ label: 'Comercial' }, { label: 'Assinantes' }]}
        actions={podeCriar ? <Button onClick={() => navigate('/commercial/subscribers/novo')} iconLeft={<Plus size={14} />}>Novo assinante</Button> : undefined}
      />
      {/* Grupo de botões por tipo — padrão Todos. */}
      <div className="mb-4 grid w-full max-w-md grid-cols-3 overflow-hidden rounded-md ring-1 ring-gray-200">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => { setTypeFilter(f.value); setPage(1) }}
            className={`cursor-pointer px-3 py-1.5 text-center text-sm transition ${
              typeFilter === f.value ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="subscribers.list"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={rowActions}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!reissue}
        onClose={() => setReissue(null)}
        onConfirm={confirmReissue}
        loading={busy}
        title="Gerar 2ª via do cartão?"
        description={`Será gerado um NOVO número de carteirinha para "${reissue?.name}". O número anterior deixa de valer.`}
        confirmLabel="Gerar"
      />

      {editDep && (
        <DependentEditModal
          dependent={editDep}
          onClose={() => setEditDep(null)}
          onSaved={() => { setEditDep(null); refresh() }}
        />
      )}

      {unlinkTarget && (
        <UnlinkCompanyModal
          titular={unlinkTarget}
          onClose={() => setUnlinkTarget(null)}
          onDone={() => { setUnlinkTarget(null); refresh() }}
        />
      )}

      {changeTarget && (
        <ChangeCompanyModal
          titular={changeTarget}
          onClose={() => setChangeTarget(null)}
          onDone={() => { setChangeTarget(null); refresh() }}
        />
      )}

      {linkTarget && (
        <ChangeCompanyModal
          titular={linkTarget}
          linking
          onClose={() => setLinkTarget(null)}
          onDone={() => { setLinkTarget(null); refresh() }}
        />
      )}

      {batchTarget && (
        <BatchInvoicesModal
          titular={batchTarget}
          onClose={() => setBatchTarget(null)}
          onDone={() => { setBatchTarget(null); refresh() }}
        />
      )}

      {cardTarget && (
        <CreditCardModal
          titular={cardTarget}
          onClose={() => setCardTarget(null)}
          onDone={() => { setCardTarget(null); refresh() }}
        />
      )}

      <ConfirmDialog
        open={!!boletoTarget}
        onClose={() => setBoletoTarget(null)}
        onConfirm={confirmSwitchBoleto}
        loading={busy}
        title="Mudar para boleto?"
        description={`A assinatura de "${boletoTarget?.name ?? ''}" será cancelada no Asaas e a forma de pagamento passará para boleto. Só será alterado se o cancelamento for bem-sucedido.`}
        confirmLabel="Mudar para boleto"
      />

      {makeDepTarget && (
        <MakeDependentModal
          subscriber={makeDepTarget}
          onClose={() => setMakeDepTarget(null)}
          onDone={() => { setMakeDepTarget(null); refresh() }}
        />
      )}

      {makeHolderTarget && (
        <MakeHolderModal
          subscriber={makeHolderTarget}
          onClose={() => setMakeHolderTarget(null)}
          onDone={() => { setMakeHolderTarget(null); refresh() }}
        />
      )}

      {sf.drawer}
    </>
  )
}

// ---------------------------------------------------------------------------
// Tornar titular em dependente — escolhe o titular destino + grau de parentesco.
// ---------------------------------------------------------------------------
function MakeDependentModal({ subscriber, onClose, onDone }: {
  subscriber: Subscriber
  onClose: () => void
  onDone: () => void
}) {
  const [targetId, setTargetId] = useState('')
  const [targetLabel, setTargetLabel] = useState('')
  const [degrees, setDegrees] = useState<DegreeRelation[]>([])
  const [degreeId, setDegreeId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { LookupService.degreeRelationships().then(setDegrees).catch(() => {}) }, [])

  // Só titulares ativos podem receber um dependente — a conversão recusa o
  // resto. E a lista só sai a partir de 2 caracteres: sem termo o endpoint
  // devolve os 50 primeiros nomes em ordem alfabética, que pareciam resultado
  // de busca.
  async function loadTargets(q: string): Promise<AsyncOption[]> {
    if (q.trim().length < 2) return []
    const r = await api.get('/commercial/subscribers/simple-list', { params: { q, type: 'TITULAR' } })
    return (r.data?.data ?? [])
      .filter((s: { id: string }) => s.id !== subscriber.id)
      .map((s: { id: string; name: string; cpf?: string }) => ({ value: s.id, label: `${s.name}${s.cpf ? ` · ${s.cpf}` : ''}` }))
  }

  async function save() {
    if (!targetId) { toast.error({ title: 'Titular obrigatório', description: 'Escolha o titular de destino.' }); return }
    if (!degreeId) { toast.error({ title: 'Parentesco obrigatório', description: 'Escolha o grau de parentesco.' }); return }
    setSaving(true)
    try {
      await SubscribersService.makeDependent(subscriber.id, { target_holder_id: targetId, degree_relationship_id: degreeId })
      toast.success({ title: 'Convertido em dependente', description: `${subscriber.name} agora é dependente.` })
      onDone()
    } catch (e) {
      toast.error({ title: 'Não foi possível converter', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal open onClose={onClose} title={`Tornar dependente — ${subscriber.name}`}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={save} loading={saving}>Converter</Button></>}>
      <div className="space-y-4">
        <p className="text-[11px] text-gray-500">
          O titular passa a ser dependente do titular escolhido. A cobrança individual dele no Asaas é encerrada (faturas anteriores ficam no histórico).
          Titulares com dependentes não podem ser convertidos.
        </p>
        <AsyncSelect label="Titular de destino" required value={targetId} selectedLabel={targetLabel}
          loadOptions={loadTargets} onChange={(v, o) => { setTargetId(v); setTargetLabel(o?.label ?? '') }}
          placeholder="Busque o titular"
          searchPlaceholder="Nome, CPF ou código do titular" />
        <Select label="Grau de parentesco" required value={degreeId} onChange={(e) => setDegreeId(e.target.value)}
          options={degrees.map(d => ({ value: d.id, label: d.degreeRelationship }))} placeholder="Selecione" />
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Tornar dependente em titular — pede plano + responsável.
// ---------------------------------------------------------------------------
function MakeHolderModal({ subscriber, onClose, onDone }: {
  subscriber: Subscriber
  onClose: () => void
  onDone: () => void
}) {
  const [plans, setPlans] = useState<{ id: string; name: string }[]>([])
  const [planId, setPlanId] = useState('')
  const [managers, setManagers] = useState<{ id: string; name: string; type: string }[]>([])
  const [managerId, setManagerId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    PlansService.list({ size: 0, status: 'ATIVO' } as never)
      .then((r) => setPlans(r.data.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => setPlans([]))
    SubscribersService.managerOptions()
      .then((groups) => setManagers(groups.flatMap(g => g.users.map(u => ({ id: u.id, name: u.name, type: g.type })))))
      .catch(() => setManagers([]))
  }, [])

  async function save() {
    if (!planId) { toast.error({ title: 'Plano obrigatório', description: 'Escolha o plano do novo titular.' }); return }
    if (!managerId) { toast.error({ title: 'Responsável obrigatório', description: 'Escolha o responsável (vendedor).' }); return }
    setSaving(true)
    try {
      await SubscribersService.makeHolder(subscriber.id, { plan_id: planId, manager_id: managerId, payment_method: 'BOLETO' })
      toast.success({ title: 'Convertido em titular', description: `${subscriber.name} agora é titular.` })
      onDone()
    } catch (e) {
      toast.error({ title: 'Não foi possível converter', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal open onClose={onClose} title={`Tornar titular — ${subscriber.name}`}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={save} loading={saving}>Converter</Button></>}>
      <div className="space-y-4">
        <p className="text-[11px] text-gray-500">
          O dependente passa a ser titular individual (forma de pagamento: boleto). Escolha o plano e o responsável.
        </p>
        <Select label="Plano" required value={planId} onChange={(e) => setPlanId(e.target.value)}
          options={plans.map(p => ({ value: p.id, label: p.name }))} placeholder="Selecione o plano" />
        <Select label="Responsável (vendedor)" required value={managerId} onChange={(e) => setManagerId(e.target.value)}
          options={managers.map(m => ({ value: m.id, label: `${m.name} · ${m.type}` }))} placeholder="Selecione o responsável" />
      </div>
    </Modal>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-800 text-right">{value}</dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Modal de edição do dependente (somente os dados dele).
// ---------------------------------------------------------------------------
function DependentEditModal({ dependent, onClose, onSaved }: {
  dependent: Subscriber
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<DependentPayload>({
    name: dependent.name,
    cpf: dependent.cpf ?? '',
    rg: dependent.rg ?? '',
    birth_date: dependent.birthDate ?? '',
    gender: (dependent.gender as DependentPayload['gender']) ?? null,
    marital_status_id: dependent.maritalStatusId ?? null,
    degree_relationship_id: dependent.degreeRelationshipId ?? null,
  })
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatus[]>([])
  const [degrees, setDegrees] = useState<DegreeRelation[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    LookupService.maritalStatuses().then(setMaritalStatuses).catch(() => {})
    LookupService.degreeRelationships().then(setDegrees).catch(() => {})
  }, [])

  const set = <K extends keyof DependentPayload>(k: K, v: DependentPayload[K]) => setForm(p => ({ ...p, [k]: v }))

  async function save() {
    if (!form.name.trim()) {
      toast.error({ title: 'Nome obrigatório', description: 'Informe o nome do dependente.' })
      return
    }
    if (!form.cpf || form.cpf.replace(/\D+/g, '').length !== 11) {
      toast.error({ title: 'CPF obrigatório', description: 'Informe um CPF válido (11 dígitos).' })
      return
    }
    if (!form.birth_date) {
      toast.error({ title: 'Data de nascimento obrigatória', description: 'Informe a data de nascimento.' })
      return
    }
    if (!dependent.parentId) {
      toast.error({ title: 'Dependente inválido', description: 'Dependente sem titular vinculado.' })
      return
    }
    setSaving(true)
    try {
      await SubscribersService.updateDependent(dependent.parentId, dependent.id, {
        name: form.name.trim(),
        cpf: form.cpf ? form.cpf.replace(/\D+/g, '') : null,
        rg: form.rg || null,
        birth_date: form.birth_date || null,
        gender: form.gender || null,
        marital_status_id: form.marital_status_id || null,
        degree_relationship_id: form.degree_relationship_id || null,
      })
      toast.success({ title: 'Dependente atualizado', description: form.name })
      onSaved()
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Editar dependente — ${dependent.name}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} loading={saving}>Salvar</Button>
      </>}
    >
      <div className="space-y-4">
        <Input label="Nome" required value={form.name} onChange={(e) => set('name', e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="CPF" required value={maskCpfPartial(form.cpf)} onChange={(e) => set('cpf', e.target.value.replace(/\D+/g, ''))} />
          <Input label="RG" value={form.rg ?? ''} onChange={(e) => set('rg', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Data de nascimento" type="date" required value={form.birth_date ?? ''} onChange={(e) => set('birth_date', e.target.value)} />
          <Select label="Gênero" value={form.gender ?? ''} onChange={(e) => set('gender', (e.target.value || null) as DependentPayload['gender'])} options={GENDER_OPTIONS} placeholder="Selecione" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Estado civil" value={form.marital_status_id ?? ''} onChange={(e) => set('marital_status_id', e.target.value || null)} options={maritalStatuses.map(m => ({ value: m.id, label: m.description || m.maritalStatus }))} placeholder="Selecione" />
          <Select label="Parentesco" value={form.degree_relationship_id ?? ''} onChange={(e) => set('degree_relationship_id', e.target.value || null)} options={degrees.map(d => ({ value: d.id, label: d.degreeRelationship }))} placeholder="Selecione" />
        </div>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Desvincular empresa — escolhe o plano individual que o titular vai assumir.
// ---------------------------------------------------------------------------
function UnlinkCompanyModal({ titular, onClose, onDone }: {
  titular: Subscriber
  onClose: () => void
  onDone: () => void
}) {
  const [plans, setPlans] = useState<EligiblePlan[]>([])
  const [planId, setPlanId] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setLoading(true)
    SubscribersService.eligiblePlans(titular.id)
      .then(setPlans)
      .catch(() => setPlans([]))
      .finally(() => setLoading(false))
  }, [titular.id])

  async function save() {
    if (!planId) {
      toast.error({ title: 'Plano obrigatório', description: 'Escolha o plano que o titular vai assumir.' })
      return
    }
    setSaving(true)
    try {
      await SubscribersService.unlinkCompany(titular.id, planId)
      toast.success({ title: 'Empresa desvinculada', description: `${titular.name} agora é individual.` })
      onDone()
    } catch (e) {
      toast.error({ title: 'Não foi possível desvincular', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Desvincular de ${titular.company?.name ?? 'empresa'}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} loading={saving} disabled={loading}>Desvincular</Button>
      </>}
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Escolha o plano que <strong>{titular.name}</strong> vai assumir como assinante individual.
          Apenas planos compatíveis com os dependentes são exibidos.
        </p>
        <Select
          label="Plano"
          required
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          options={plans.map(p => ({ value: p.id, label: `${p.name}${p.modality ? ` · ${p.modality}` : ''} — ${p.formatted.price}` }))}
          placeholder={loading ? 'Carregando' : (plans.length ? 'Selecione o plano' : 'Nenhum plano compatível')}
          disabled={loading || plans.length === 0}
        />
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Trocar empresa — escolhe a empresa e, então, um plano dela.
// ---------------------------------------------------------------------------
function ChangeCompanyModal({ titular, onClose, onDone, linking = false }: {
  titular: Subscriber
  onClose: () => void
  onDone: () => void
  /** true = vincular (titular individual); false = trocar (já tem empresa). */
  linking?: boolean
}) {
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyId, setCompanyId] = useState('')
  const [plans, setPlans] = useState<EligiblePlan[]>([])
  const [planId, setPlanId] = useState('')
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    CompaniesService.list({ size: 0, status: 'ATIVO' } as never)
      .then(r => setCompanies(r.data.filter(c => c.id !== titular.companyId)))
      .catch(() => setCompanies([]))
  }, [titular.companyId])

  // Ao escolher a empresa, carrega os planos dela (filtrados pelos dependentes).
  useEffect(() => {
    if (!companyId) { setPlans([]); setPlanId(''); return }
    setLoadingPlans(true); setPlanId('')
    SubscribersService.eligiblePlans(titular.id, companyId)
      .then(setPlans)
      .catch(() => setPlans([]))
      .finally(() => setLoadingPlans(false))
  }, [companyId, titular.id])

  async function save() {
    if (!companyId) { toast.error({ title: 'Empresa obrigatória', description: 'Escolha a nova empresa.' }); return }
    if (!planId) { toast.error({ title: 'Plano obrigatório', description: 'Escolha o plano da nova empresa.' }); return }
    setSaving(true)
    try {
      await SubscribersService.changeCompany(titular.id, companyId, planId)
      toast.success({ title: linking ? 'Empresa vinculada' : 'Empresa alterada', description: `${titular.name} foi vinculado à empresa.` })
      onDone()
    } catch (e) {
      toast.error({ title: linking ? 'Não foi possível vincular' : 'Não foi possível trocar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${linking ? 'Vincular empresa' : 'Trocar empresa'} — ${titular.name}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} loading={saving}>{linking ? 'Vincular' : 'Trocar'}</Button>
      </>}
    >
      <div className="space-y-3">
        <Select
          label="Empresa"
          required
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          options={companies.map(c => ({ value: c.id, label: c.name }))}
          placeholder="Selecione a empresa"
        />
        <Select
          label="Plano"
          required
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          options={plans.map(p => ({ value: p.id, label: `${p.name}${p.modality ? ` · ${p.modality}` : ''} — ${p.formatted.price}` }))}
          placeholder={!companyId ? 'Escolha a empresa primeiro' : (loadingPlans ? 'Carregando' : (plans.length ? 'Selecione o plano' : 'Nenhum plano compatível'))}
          disabled={!companyId || loadingPlans || plans.length === 0}
        />
        {companyId && (
          <p className="text-[11px] text-gray-500">Apenas planos compatíveis com os dependentes são exibidos.</p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Gerar faturas em lote — intervalo mês/ano inicial → final.
// ---------------------------------------------------------------------------
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
].map((m, i) => ({ value: String(i + 1), label: m }))

function BatchInvoicesModal({ titular, onClose, onDone }: {
  titular: Subscriber
  onClose: () => void
  onDone: () => void
}) {
  const now = new Date()
  const [startMonth, setStartMonth] = useState(String(now.getMonth() + 1))
  const [startYear, setStartYear] = useState<number | null>(now.getFullYear())
  const [endMonth, setEndMonth] = useState(String(now.getMonth() + 1))
  const [endYear, setEndYear] = useState<number | null>(now.getFullYear())
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!startYear || !endYear) {
      toast.error({ title: 'Período obrigatório', description: 'Informe o ano inicial e final.' })
      return
    }
    setSaving(true)
    try {
      const res = await SubscribersService.generateInvoicesRange(titular.id, {
        start_month: Number(startMonth), start_year: startYear,
        end_month: Number(endMonth), end_year: endYear,
      })
      if (res.failed && res.failed > 0) {
        toast.error({
          title: `${res.failed} fatura(s) NÃO geradas`,
          description: `Falha na cobrança do Asaas — não foram criadas. Veja Integrações → Logs de erro. (${res.created} criada(s), ${res.skipped} ignorada(s)).`,
        })
      } else {
        toast.success({ title: 'Faturas geradas', description: `${res.created} criada(s), ${res.skipped} ignorada(s) (já existiam).` })
      }
      onDone()
    } catch (e) {
      toast.error({ title: 'Não foi possível gerar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Gerar faturas em lote — ${titular.name}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} loading={saving}>Gerar</Button>
      </>}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Serão geradas faturas para cada mês do intervalo. Competências (mês/ano) que já possuem fatura são ignoradas.
        </p>
        <div>
          <div className="mb-1 text-xs font-medium text-gray-700">De</div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} options={MONTHS} searchable={false} clearable={false} />
            <NumberInput value={startYear} onChange={setStartYear} decimals={0} min={2020} max={2100} />
          </div>
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-gray-700">Até</div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={endMonth} onChange={(e) => setEndMonth(e.target.value)} options={MONTHS} searchable={false} clearable={false} />
            <NumberInput value={endYear} onChange={setEndYear} decimals={0} min={2020} max={2100} />
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Mudar para cartão de crédito — cria assinatura no Asaas (cartão não é gravado).
// ---------------------------------------------------------------------------
function maskCardNumber(v: string): string {
  return (v.replace(/\D+/g, '').slice(0, 19).match(/.{1,4}/g) ?? []).join(' ')
}
function maskExpiry(v: string): string {
  const d = v.replace(/\D+/g, '').slice(0, 4)
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d
}

function CreditCardModal({ titular, onClose, onDone }: {
  titular: Subscriber
  onClose: () => void
  onDone: () => void
}) {
  const [holder, setHolder] = useState('')
  const [number, setNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvv, setCvv] = useState('')
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!holder.trim() || number.replace(/\D+/g, '').length < 13 || expiry.length < 5 || cvv.length < 3) {
      toast.error({ title: 'Dados do cartão', description: 'Preencha nome, número, validade (MM/AA) e CVV.' })
      return
    }
    setSaving(true)
    try {
      await SubscribersService.switchToCreditCard(titular.id, {
        card_holder_name: holder.trim(),
        card_number: number.replace(/\D+/g, ''),
        expiry,
        cvv,
      })
      toast.success({ title: 'Forma de pagamento alterada', description: 'Assinatura criada no Asaas (cartão de crédito).' })
      onDone()
    } catch (e) {
      toast.error({ title: 'Não foi possível alterar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Mudar para cartão de crédito — ${titular.name}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} loading={saving}>Confirmar</Button>
      </>}
    >
      <div className="space-y-4">
        <p className="text-[11px] text-gray-500">
          Os dados do cartão são enviados ao Asaas e não são armazenados. A forma de pagamento só muda se a assinatura for criada.
        </p>
        <Input label="Nome impresso no cartão" required value={holder} onChange={(e) => setHolder(e.target.value)} />
        <Input label="Número do cartão" required value={maskCardNumber(number)} onChange={(e) => setNumber(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Validade (MM/AA)" required value={maskExpiry(expiry)} onChange={(e) => setExpiry(maskExpiry(e.target.value))} placeholder="MM/AA" />
          <Input label="CVV" required value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D+/g, '').slice(0, 4))} />
        </div>
      </div>
    </Modal>
  )
}
